/**
 * creates a contextual fragment based on given markup
 * using a DocumentFragment is sometimes necessary so that inline <script> tags inside of `markup` are getting executed
 * e.g. for responsive images ratios we use inline javascript rendered in the templates
 *
 * @see https://developer.mozilla.org/en-US/docs/Web/API/DocumentFragment
 * @param {string} markup - html markup
 * @returns {DocumentFragment} the contextual fragment
 */

const convertHtmlStringToElements = (markup) => {
  return document.createRange().createContextualFragment(markup);
};

/**
 * removes unit like 'px', 'ms' or 'rem' from value
 *
 * @param {string} value - value with unit
 * @returns {number} value without unit
 */
const removeUnitFromValue = (value) => {
  return Number(value.replace(/[^0-9]/g, ""));
};

/**
 * scrolls an element to the top of the viewport
 * only modern browser will be able to do a smooth scroll
 *
 * @param {HTMLElement} element - html element
 * @param {string} behavior - scroll behavior, default is 'smooth'
 * @param {number} timeout - timeout in ms, default is 0
 */
const scrollToElement = (element, behavior = "smooth", timeout = 0) => {
  setTimeout(() => {
    const header = document.querySelector(".Header");
    const headerHeight = header ? header.offsetHeight : 0;

    const scrollTop =
      element.getBoundingClientRect().top + window.pageYOffset - headerHeight;

    window.scrollTo({ top: scrollTop, behavior });
  }, timeout);
};

/**
 * adds smooth increase of any number from zero to a specified value (`value`) at a specified speed (`duration`)
 *
 * @param {HTMLElement} element - element which should be animated
 * @param {number} value - final number
 * @param {number} duration - duration of the animation in ms
 * @param {boolean} property - element property that should be changed e.g. innerHTML or value
 */
const animateNumber = (element, value, duration, property = "innerHTML") => {
  const startTime = performance.now();
  const step = (currentTime) => {
    const progress = Math.min((currentTime - startTime) / duration, 1);
    // use float numbers in the animation if the original value is a float number
    // print with "," instead of "." for float numbers
    if (value % 1 === 0) {
      // formatting count number to german standard decimal number format. e.g. 1000 -> 1.000
      // eslint-disable-next-line no-param-reassign
      element[property] = Math.trunc(progress * value).toLocaleString("de");
    } else {
      const val = Math.round(progress * value * 10) / 10;
      // eslint-disable-next-line no-param-reassign
      element[property] =
        property === "innerHTML" ? String(val).replace(".", ",") : val;
    }

    if (progress < 1) {
      window.requestAnimationFrame(step);
    } else {
      window.cancelAnimationFrame(window.requestAnimationFrame(step));
    }
  };

  window.requestAnimationFrame(step);
};

export {
  animateNumber,
  convertHtmlStringToElements,
  removeUnitFromValue,
  scrollToElement,
};
